const { flatten, uniq } = require('lodash')
const { getStock, getSupplierFromStock } = require('@balibart/stocks')
const { getDelays } = require('./getDeliveryDelays')
const { countries, regexps, validator } = require('./countries')
const formatPhoneNumber = require('./phone')
const logger = require('./logger')

const defaultDelays = {
  deliveryMin: 5,
  deliveryMax: 7,
}

function getContribution(products) {
  // WE GET ALL DELIVERYCONTRIBUTION ( AKA WEIGHT ) TO GET THE TOTAL ENVOY WEIGHT
  const weight = products
    .map(p => p.baseProduct.deliveryContribution * (p.quantity || 1))
    .reduce((a, c) => a + c, 0)
  return weight
}

function getDeliveryPrice(country, mode, supplier, pricePerContribution) {
  try {
    return supplier.deliveryPrices.find(pr => pr.countries.includes(country) &&
    pr.pricePerContribution === pricePerContribution &&
    pr.mode === mode) || false
  } catch (e) {
    return false
  }
}


// USE TO GET DELIVERY DELAYS FOR EACH SUBORDER
function getDeliveryPricesFromSupplier(supplier, products, shop, selfSend = '') {
  const { deliveryPrices = [] } = supplier
  const hasShopDeliveryPrices = shop &&
    shop.settings &&
    shop.settings.deliveryPrices &&
    shop.settings.deliveryPrices.length

  const hasProductDeliveryPrices = products &&
    products.map(p => p.deliveryPrices && p.deliveryPrices.length)

  if (supplier._id === selfSend) {
    return shop.settings.selfSendDeliveryPrices
  } if (hasShopDeliveryPrices) {
    return deliveryPrices.concat(hasShopDeliveryPrices)
  } if (hasProductDeliveryPrices) {
    return deliveryPrices.concat(hasProductDeliveryPrices)
  }
  return deliveryPrices
}

function getDeliveryPrices(country, mode, supplier, contribution) {
  const { deliveryPrices = [] } = supplier
  const price = deliveryPrices.filter(
    pr => pr.countries.includes(country) &&
    (pr.weightLimitForThatPrice >= contribution) && (mode === 'all' ? true : mode === pr.mode),
  )
  return price
}

function getMinimumPrice(deliveryPrices) {
  const min = Math.min(...deliveryPrices.map(pr => pr.pricePerContribution))
  return deliveryPrices.find(pr => pr.pricePerContribution === min)
}

function getDelaysForSubOrders(supplierFromDb, products, assignedProducts, shop, country, mode, stocks) {
  try {
    const contribution = getContribution(assignedProducts)
    const deliveryPrices = getDeliveryPricesFromSupplier(supplierFromDb, products, shop)
    const supplierWithPrices = { ...supplierFromDb, deliveryPrices }
    const prices = getDeliveryPrices(country, mode, supplierWithPrices, contribution)
    const minimumPrice = getMinimumPrice(prices)
    const allDelays = products.map(p => getDelays(p, p.baseProduct, minimumPrice, stocks))
    return {
      deliveryMin: Math.max(...allDelays.map(d => d.deliveryMin || defaultDelays.deliveryMin)),
      deliveryMax: Math.max(...allDelays.map(d => d.deliveryMax || defaultDelays.deliveryMax)),
    }
  } catch (e) {
    return {
      deliveryMin: defaultDelays.deliveryMin,
      deliverymax: defaultDelays.deliveryMax,
    }
  }
}


function splitProductsBySupplier(suppliers, products, shop, selfSend = '', stocks) {
  try {
    const result = suppliers.map((supplier) => {
      // WE GET PRODUCTS BY SUPPLIER
      const supplierProducts = products.filter((p) => {
        const stock = getStock(p, p.variations, stocks)
        const supplierFromStock = getSupplierFromStock(stock, p)
        return supplierFromStock === String(supplier._id) &&
      (!p.deliveryPrices || !p.deliveryPrices.length)
      })
      if (!supplierProducts || !supplierProducts.length) {
        return false
      }
      const { deliveryPrices = [] } = supplier
      if (
        shop && shop.settings &&
      shop.settings.deliveryPrices &&
      shop.settings.deliveryPrices.length
      ) {
        deliveryPrices.push(...shop.settings.deliveryPrices)
      }
      // In case of self send , we get deliveryPrices from shop.settings.selfSendDeliveryPrices
      const isSelfSend = String(supplier._id) === selfSend
      return {
        ...supplier,
        deliveryPrices: isSelfSend ? shop.settings.selfSendDeliveryPrices : deliveryPrices,
        products: supplierProducts,
        weight: getContribution(supplierProducts),
      }
    }).filter(Boolean)
    const productWithDeliveries = products.filter(p => p.deliveryPrices && p.deliveryPrices.length)
    const productWithDeliveriesAsSupplier = productWithDeliveries.map(p => ({
      name: p.name,
      deliveryPrices: p.deliveryPrices,
    }))
    result.concat(productWithDeliveriesAsSupplier)
    return result
  } catch (e) {
    logger.log({ message: 'splitProductsBySupplier', level: 'err', e })
    throw e
  }
}


function isOverWeight(supplierWithProducts, mode, country) {
  try {
  // MINIMUM LENGTH ?
    const { deliveryPrices } = supplierWithProducts
    const hasPrice = deliveryPrices.filter(price => price.countries.includes(country) &&
  price.mode === mode)
    return hasPrice.length
  } catch (e) {
    return false
  }
}


function calculatePrices(products, suppliers, country, mode = 'standard', shop, stocks) {
  try {
    const cannotBeDelivered = []
    const isOverMaximumWeight = []
    const splittedProductsBySupplier = splitProductsBySupplier(suppliers, products, shop, '', stocks)
    if (!splittedProductsBySupplier.length) {
      throw Error('cannot split products')
    }
    const result = splittedProductsBySupplier.map((supplier) => {
      let supplierPrices = getDeliveryPrices(country, mode, supplier, supplier.weight)
      if (!supplierPrices.length) {
        if (mode !== 'standard') {
          supplierPrices = getDeliveryPrices(country, 'standard', supplier, supplier.weight)
        } else {
          // CHECK FOR OVERWEIGHT
          (isOverWeight(supplier, mode, country) ?
            isOverMaximumWeight : cannotBeDelivered).push(
            ...supplier.products.map(p => String(p._id || p.id)),
          )
          return false
        }
      }
      if (!supplierPrices.length) {
        // CHECK FOR OVERWEIGHT
        (isOverWeight(supplier, mode, country) ?
          isOverMaximumWeight : cannotBeDelivered).push(
          ...supplier.products.map(p => String(p._id || p.id)),
        )
        return false
      }
      // WE GET THE MINIMUM PRICE
      const minimumPrice = getMinimumPrice(supplierPrices)
      const allDelays = supplier.products.map(p => getDelays(p, p.baseProduct, minimumPrice, stocks))

      const { pricePerContribution } = minimumPrice

      return {
        price: pricePerContribution,
        delays: {
          deliveryMin: Math.max(...allDelays.map(d => d.deliveryMin || defaultDelays.deliveryMin)),
          deliveryMax: Math.max(...allDelays.map(d => d.deliveryMax || defaultDelays.deliveryMax)),
        },
      }
    })
    // IF SOME PRODUCTS CANNOT BE DELIVERED , WE THROW AN ERROR
    if (
      (cannotBeDelivered && cannotBeDelivered.length) ||
      (isOverMaximumWeight && isOverMaximumWeight.length)) {
      const error = {
        error: 'cannot be delivered',
        nonDeliverable: uniq(cannotBeDelivered),
        isOverWeight: uniq(isOverMaximumWeight),
      }
      throw error
    }
    // WE REDUCE ALL THE RESULT TO GET ONE PRICE ...
    return {
      price: result.reduce((a, c) => a + c.price, 0),
      delays:
         {
           deliveryMin: Math.max(...result.map(r => r.delays.deliveryMin)),
           deliveryMax: Math.max(...result.map(r => r.delays.deliveryMax)),
         },
    }
  } catch (e) {
    logger.log('caldulatePrices', e)
    throw e
  }
}

function checkModeAvailability(deliveryModes, supplier, products, country, stocks) {
  // WE ARE MAPPING ON EACH DELIVERY MODE
  return deliveryModes.map((m) => {
    const contribution = getContribution(products)
    // IF WE GET NO PRICE THE MODE IS UNAVAILABLE FOR THE SUPPLIER
    const available = getDeliveryPrices(country, m, supplier, contribution)
    if (!available || !available.length) {
      return { type: m, unavailable: true }
    }

    const matchedPrice = getMinimumPrice(available)

    const { pricePerContribution } = matchedPrice || {}
    const allDelays = products.map(p => getDelays(p, p.baseProduct, matchedPrice, stocks))
    return {
      type: m,
      price: pricePerContribution,
      deliveryMin: Math.max(...allDelays.map(d => d.deliveryMin || defaultDelays.deliveryMin)),
      deliveryMax: Math.max(...allDelays.map(d => d.deliveryMax || defaultDelays.deliveryMax)),
    }
  })
}

function calculateShippingPrice(cart, shop, dbProducts, dbSuppliers, assignedProducts, stocks) {
  try {
    const { address } = cart
    const type = cart.checkout ? cart.checkout.deliveryMode : cart.deliveryMode
    if (!address || !address.country) {
      throw new Error('no address')
    }
    const country = address && typeof address.country === 'string' ?
      address.country :
      address.country.value
    if (!cart.products.length) {
      return {
        price: 0,
        delays: {
          deliveryMin: 0,
          deliveryMax: 0,
        },
      }
    }
    // const ids = cart.products.map(p => String(p.id || p._id))
    // WE CHECK IF ALL CART PRODUCTS ARE FOUND IN DATABASE
    // if (!ids.length > dbProducts.length) { throw new Error('some products not found') }
    // WE NEED PRODUCTS WITH BASEPRODUCTS ASSIGNED
    // (WE ASSGNINED IT IN PRICING BUT CALCUILATESHIPPING PRICE IS ACTUALLY CALLEDO OUTSIDE PRICING)
    const delivery = calculatePrices(cart.products.map(product => ({
      ...product,
      baseProduct: assignedProducts.find(p => p._id === product._id).baseProduct,
    })), dbSuppliers, country, type, shop, stocks)
    if (!delivery || typeof delivery.price !== 'number') { throw new Error('no pricing') }
    return delivery
  } catch (e) {
    logger.log({ message: 'calculateShippingPrice', level: 'err', e })
    throw e
  }
}


// TODO ADD DELAYS
function getDeliveryPriceForAllModes(shop, cart, assignedProducts, suppliers, stocks) {
  try {
    // const shop = await database().collection('Shops').findOne({
    //   _id: objectID(cart.seller),
    // })

    // We merged products with bps
    // const assignedProducts = await assignBpsToProducts(cart.products)
    // we get all suppliers
    // const suppliers = await database().collection('Suppliers').find({
    // }).toArray()
    const country = cart.address.country.value || cart.address.country
    // we split products for each suppliers
    const splittedProductsBySupplier = splitProductsBySupplier(suppliers, assignedProducts, shop, stocks)
    // We get all modes for future usage
    // We are mapping on each suppliers to manage deliveryPrice for each modes
    const suppliersWithTypes = splittedProductsBySupplier.map((s) => {
      const { deliveryPrices } = s
      // We get all deliveryModes  for this supplier
      const deliveryModes = uniq(deliveryPrices.map(d => d.mode))

      // We are mapping on this mode to get the status ( available or not)
      const modes = checkModeAvailability(deliveryModes, s, s.products, country)
      return {
        ...s,
        modes,
      }
    })
    // WE SHOULD RECONCILIATE ALL SUPPLIER, ONE SHOULD BE UNAVAILABLE ON PICKUP BUT THE OTHER ISNT
    const availableModesInCart = uniq(flatten(suppliersWithTypes.map((s) => s.modes.map(d => d.type))))
    // WE MAP ON EACH MODES AVAILABLE AND SEARCH EACH ONE IN EACH SUPPLIER
    const reconciliatedModes = availableModesInCart.map((m) => {
      const allConcernedModes = suppliersWithTypes.map(s => s.modes.find(
        mode => mode.type === m,
      ))
      // WE FILTER UNAVAILABLE MODS
      const valids = allConcernedModes.filter(mo => !mo.unavailable)
      // IF NO VALIDS , THE MODES IN UNAVAILABLE FOR ALL SUPPLIERS
      if (!valids || !valids.length) {
        return { type: m, unavailable: true }
      }
      // ELSE WE AGGREGATE ALL PRICES
      const value = valids.reduce((a, c) => a + c.price, 0)
      // AND WE GET DELAYS ( FALLBACK TO DEFAULT DELAYS IF NOT PRESENT IN SUPPLIER OBJECT)
      const delays = valids.map((v) => {
        const {
          deliveryMin = defaultDelays.deliveryMin,
          deliveryMax = defaultDelays.deliveryMax,
        } = v
        return { deliveryMin, deliveryMax }
      })
      return {
        type: m,
        price: value,
        deliveryMin: Math.max(...delays.map(d => d.deliveryMin)),
        deliveryMax: Math.max(...delays.map(d => d.deliveryMax)),
      }
    })
    return reconciliatedModes
  } catch (e) {
    logger.log('getDeliveryPricesForAllModes', e)
    return [{}]
  }
}

module.exports = {
  calculateShippingPrice,
  getDeliveryPrices,
  getDeliveryPriceForAllModes,
  getDeliveryPrice,
  splitProductsBySupplier,
  checkModeAvailability,
  calculatePrices,
  getDelaysForSubOrders,
  countries,
  regexps,
  validator,
  formatPhoneNumber,
}
